import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, CheckCircle, XCircle } from 'lucide-react';
import './AvailabilityTab.css';

const AvailabilityTab = ({ hotelId, token }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (hotelId && token) {
      fetchRooms();
    }
  }, [hotelId, token, selectedDate]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/manager/rooms?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const roomsList = Array.isArray(response.data?.data?.rooms)
        ? response.data.data.rooms
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

      setRooms(roomsList);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setRooms([]);
      setLoading(false);
    }
  };

  const normalizeAvailabilityStatus = (room) => {
    if (room?.current_booking) return 'booked';

    const value = String(room?.status || '').toLowerCase().trim();
    if (value === 'occupied' || value === 'reserved') return 'booked';
    if (value === 'cleaning') return 'maintenance';
    if (value === 'out-of-service') return 'out_of_service';
    if (['available', 'booked', 'maintenance', 'out_of_service'].includes(value)) return value;
    return 'available';
  };

  const getAvailabilityStatus = (room) => {
    const status = normalizeAvailabilityStatus(room);

    if (status === 'available') return { key: 'available', label: 'Available', color: '#10b981', icon: CheckCircle };
    if (status === 'booked') return { key: 'booked', label: 'Booked', color: '#ef4444', icon: XCircle };
    if (status === 'maintenance') return { key: 'maintenance', label: 'Maintenance', color: '#f59e0b', icon: Calendar };
    if (status === 'out_of_service') return { key: 'out_of_service', label: 'Out of Service', color: '#6b7280', icon: XCircle };

    return { key: status, label: status, color: '#9ca3af', icon: XCircle };
  };

  if (loading) {
    return <div className="tab-loading"><div className="loading-spinner"></div><p>Loading availability...</p></div>;
  }

  const roomsList = Array.isArray(rooms) ? rooms : [];

  const legendItems = [
    { key: 'available', label: 'Available', color: '#10b981', icon: CheckCircle },
    { key: 'booked', label: 'Booked', color: '#ef4444', icon: XCircle },
    { key: 'maintenance', label: 'Maintenance', color: '#f59e0b', icon: Calendar },
    { key: 'out_of_service', label: 'Out of Service', color: '#6b7280', icon: XCircle }
  ];

  const statusCounts = roomsList.reduce((acc, room) => {
    const key = normalizeAvailabilityStatus(room);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="availability-tab">
      <div className="tab-header">
        <h2><Calendar size={24} /> Room Availability</h2>
        <input 
          type="date" 
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="date-picker"
        />
      </div>

      <div className="availability-legend">
        {legendItems.map((item) => {
          const Icon = item.icon;
          return (
            <span key={item.key}>
              <Icon size={16} style={{ color: item.color }} /> {item.label} ({statusCounts[item.key] || 0})
            </span>
          );
        })}
      </div>

      <div className="availability-grid">
        {roomsList.map((room, index) => {
          const status = getAvailabilityStatus(room);
          const StatusIcon = status.icon;
          const roomKey = room.id ?? room.room_id ?? `${room.room_number || 'room'}-${index}`;
          return (
            <div key={roomKey} className="availability-card" style={{ borderColor: status.color }}>
              <div className="card-header">
                <span className="room-number">{room.room_number}</span>
                <StatusIcon size={20} style={{ color: status.color }} />
              </div>
              <div className="card-body">
                <p className="room-type">{room.room_type}</p>
                <p className="status-label" style={{ color: status.color }}>{status.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {roomsList.length === 0 && <div className="empty-state"><Calendar size={48} /><h3>No Rooms Found</h3><p>No rooms available for this date</p></div>}
    </div>
  );
};

export default AvailabilityTab;
